import React, { useEffect, useState, useRef } from 'react';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import chipLogo from './images/Elinnos-chip.png';
import { useAuth } from './AuthWrapper';
import ApiService from '../services/api';
import Logger from '../utils/logger';

/**
 * IDELoginDirect - Handles direct IDE login with loopback callback
 * 
 * Flow:
 * 1. IDE starts a local loopback server on a random port
 * 2. IDE opens this page with ?callback_url=http://127.0.0.1:PORT/callback&state=...
 * 3. User logs in (or is already logged in)
 * 4. This page generates an auth code and redirects to the callback_url
 */
function IDELoginDirect() {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, login_required, completing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const completingRef = useRef(false); // Guard against duplicate auth completions

  useEffect(() => {
    const handleIDEAuth = async () => {
      try {
        // Get callback_url, state, and PKCE params from URL
        const params = new URLSearchParams(window.location.search);
        const callbackUrl = params.get('callback_url');
        const state = params.get('state');
        const codeChallenge = params.get('code_challenge');
        const codeChallengeMethod = params.get('code_challenge_method') || 'S256';

        if (!callbackUrl) {
          setStatus('error');
          setErrorMessage('Missing callback_url parameter. Please try signing in again from the IDE.');
          return;
        }

        // Validate callback URL is localhost
        try {
          const url = new URL(callbackUrl);
          if (url.hostname !== '127.0.0.1' && url.hostname !== 'localhost') {
            setStatus('error');
            setErrorMessage('Invalid callback URL. Must be localhost.');
            return;
          }
        } catch {
          setStatus('error');
          setErrorMessage('Invalid callback URL format.');
          return;
        }

        Logger.debug('IDE direct login', { callbackUrl, state, hasUser: !!user, authLoading });

        // Wait for auth check to complete
        if (authLoading) {
          return;
        }

        // If user is not logged in, they need to log in first
        if (!user) {
          setStatus('login_required');
          return;
        }

        // Guard: prevent duplicate completions from React re-renders
        if (completingRef.current) {
          return;
        }
        completingRef.current = true;

        // User is logged in, generate auth code and redirect
        setStatus('completing');

        const apiBaseUrl = ApiService.baseUrl;
        const response = await fetch(`${apiBaseUrl}/ide/authorize/direct`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            callback_url: callbackUrl,
            state: state,
            code_challenge: codeChallenge,
            code_challenge_method: codeChallengeMethod
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          if (response.status === 401) {
            // User not authenticated - redirect to login
            setStatus('login_required');
            return;
          }
          throw new Error(errorData.detail || `Failed to complete IDE authorization: ${response.status}`);
        }

        const { auth_code } = await response.json();
        Logger.debug('IDE auth code generated', { auth_code: auth_code.substring(0, 8) + '...' });

        // Redirect to the loopback callback with the auth code
        setStatus('success');

        // Build the redirect URL with code and state
        const redirectUrl = new URL(callbackUrl);
        redirectUrl.searchParams.set('code', auth_code);
        if (state) {
          redirectUrl.searchParams.set('state', state);
        }

        // Redirect immediately to the loopback callback — use replace() to
        // prevent the browser back-button from re-triggering the flow
        window.location.replace(redirectUrl.toString());

      } catch (error) {
        Logger.error('IDE direct auth error', { error });
        setStatus('error');
        // Provide more helpful error messages
        let message = error.message || 'An error occurred during IDE authentication.';
        if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
          message = 'Unable to connect to the server. Please check your internet connection.';
        } else if (message.includes('Not authenticated') || message.includes('401')) {
          message = 'Please log in to ChipDesigner.ai first, then try again.';
        }
        setErrorMessage(message);
      }
    };

    handleIDEAuth();
  }, [user, authLoading]);

  // If login is required, redirect to main login with return URL
  useEffect(() => {
    if (status === 'login_required') {
      // Redirect to dashboard login with return URL
      // /dashboard shows the login form via AuthWrapper
      const currentUrl = window.location.href;
      const loginUrl = `/dashboard?redirect=${encodeURIComponent(currentUrl)}`;
      
      // Use hash-based navigation for HashRouter (file:// protocol)
      if (window.location.protocol === 'file:') {
        window.location.hash = `#${loginUrl}`;
      } else {
        window.location.href = loginUrl;
      }
    }
  }, [status]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src={chipLogo} 
              alt="ChipDesigner.AI" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">ChipDesigner IDE</h1>
          <p className="text-gray-400">Sign in to connect your IDE</p>
        </div>

        {/* Status Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-8 shadow-2xl text-center">
          {(status === 'checking' || status === 'login_required') && (
            <>
              <div className="flex justify-center mb-4">
                <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">
                {status === 'checking' ? 'Checking Authentication...' : 'Redirecting to Login...'}
              </h2>
              <p className="text-gray-400">
                {status === 'checking' 
                  ? 'Please wait while we verify your session.'
                  : 'You need to sign in to connect your IDE.'}
              </p>
            </>
          )}

          {status === 'completing' && (
            <>
              <div className="flex justify-center mb-4">
                <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">Authorizing IDE...</h2>
              <p className="text-gray-400">
                Completing the connection to ChipDesigner IDE.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-600/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">IDE Connected!</h2>
              <p className="text-gray-400 mb-4">
                Your ChipDesigner IDE is now connected to your account.
              </p>
              <p className="text-gray-300 font-medium">
                Returning to the IDE...
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">Connection Failed</h2>
              <p className="text-gray-400 mb-4">
                {errorMessage}
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => window.close()}
                  className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Close Window
                </button>
              </div>
            </>
          )}
        </div>

        {/* Help text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            Having trouble? Make sure ChipDesigner IDE is running and try again.
          </p>
        </div>
      </div>
    </div>
  );
}

export default IDELoginDirect;
